import { useContext, useEffect, useState } from 'react'
import { Box, Button, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material'

import { resetSystemPrompt, updateSystemPrompt } from '../../api/api'
import { SystemPrompt } from '../../api/models'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { AppStateContext } from '../../state/AppProvider'

import styles from './SystemPrompts.module.css'

interface SystemPromptEditorProps {
  prompt: SystemPrompt
  onSaved: (prompt: SystemPrompt) => void
  onReset: (prompt: SystemPrompt) => void
}

export default function SystemPromptEditor({ prompt, onSaved, onReset }: SystemPromptEditorProps) {
  const appStateContext = useContext(AppStateContext)
  const [content, setContent] = useState(prompt.content)
  const [isSaving, setIsSaving] = useState(false)
  const [isResetting, setIsResetting] = useState(false)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)

  useEffect(() => {
    setContent(prompt.content)
  }, [prompt.key, prompt.content])

  const notify = (message: string, severity: 'success' | 'warning' | 'error') => {
    appStateContext?.dispatch({ type: 'SET_SNACKBAR_STATE', payload: { message, severity } })
  }

  const isUnchanged = content === prompt.content
  const isEmpty = content.trim() === ''
  const isSaveDisabled = isEmpty || isUnchanged || isSaving || isResetting

  const handleSave = async () => {
    if (isSaveDisabled) return
    setIsSaving(true)
    try {
      const result = await updateSystemPrompt(prompt.key, content)
      if (result.success) {
        notify('Prompt updated', 'success')
        if (result.prompt) onSaved(result.prompt)
      } else {
        notify(result.message || 'Failed to update system prompt', 'error')
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = async () => {
    setIsResetting(true)
    try {
      const result = await resetSystemPrompt(prompt.key)
      if (result.success) {
        notify('Reset to default', 'success')
        if (result.prompt) onReset(result.prompt)
      } else {
        notify(result.message || 'Failed to reset system prompt', 'error')
      }
    } finally {
      setIsResetting(false)
      setIsResetDialogOpen(false)
    }
  }

  return (
    <Box className={styles.editor}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        className={styles.editorTitleRow}>
        <Typography
          variant="h6"
          className={styles.editorTitle}>
          {prompt.name}
        </Typography>
        <Chip
          size="small"
          label={prompt.is_custom ? 'Custom' : 'Default'}
          color={prompt.is_custom ? 'primary' : 'default'}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`v${prompt.version}`}
        />
      </Stack>

      {prompt.description && (
        <Typography
          variant="body2"
          className={styles.editorDescription}>
          {prompt.description}
        </Typography>
      )}

      {prompt.template_variables.length > 0 && (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          className={styles.templateVariables}>
          {prompt.template_variables.map(variable => (
            <Chip
              key={variable}
              size="small"
              variant="outlined"
              label={`{${variable}}`}
            />
          ))}
        </Stack>
      )}

      <TextField
        value={content}
        onChange={e => setContent(e.target.value)}
        multiline
        minRows={16}
        fullWidth
        disabled={isSaving || isResetting}
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
        className={styles.contentField}
      />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        className={styles.editorActions}>
        <Button
          variant="outlined"
          color="error"
          disabled={!prompt.is_custom || isSaving || isResetting}
          onClick={() => setIsResetDialogOpen(true)}>
          Reset to default
        </Button>
        <Button
          variant="contained"
          disabled={isSaveDisabled}
          onClick={handleSave}
          startIcon={
            isSaving ? (
              <CircularProgress
                size={16}
                color="inherit"
              />
            ) : undefined
          }>
          Save
        </Button>
      </Stack>

      <CustomDialog
        open={isResetDialogOpen}
        title="Reset to default"
        setClose={() => setIsResetDialogOpen(false)}
        primaryAction={handleReset}
        primaryActionButtonText="Reset"
        primaryActionDisabled={isResetting}
        secondaryAction={() => setIsResetDialogOpen(false)}
        secondaryActionButtonText="Cancel"
        secondaryActionDisabled={isResetting}>
        <Typography variant="body1">
          This will discard the current customization and restore the default content for "{prompt.name}". This action
          cannot be undone.
        </Typography>
      </CustomDialog>
    </Box>
  )
}
